import {
  faChevronDown,
  faChevronUp,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, ReactNode, useState } from 'react';
import { useBreakpoint } from '~/hooks/useBreakpoint';

type OptionConfig = {
  /** Also used as element key, therefore must be unique. */
  label: string;
  onClick: () => void;
  disabled?: boolean;
  dataUsetiful?: string;
  /** Optional icon for the option (only used in FAB variant) */
  icon?: ReactNode;
};

type Props = {
  /**
   * The first option in the array will be the primary option.
   *
   * The rest will be the alternative options, rendered bottom to top (default) or in dropdown (fab).
   */
  options: OptionConfig[];
  /**
   * Visual variant:
   * - 'default': Full-width stacking button (original style)
   * - 'fab': Pill-shaped floating action button with dropdown
   */
  variant?: 'default' | 'fab';
  /** Additional class name for the container (fab variant only) */
  className?: string;
};

export function MultiActionButton({
  options,
  variant = 'default',
  className,
}: Props) {
  if (variant === 'fab') {
    return <FabVariant options={options} className={className} />;
  }

  return <DefaultVariant options={options} />;
}

/**
 * FAB variant: Pill-shaped split button with dropdown for secondary actions
 */
function FabVariant({
  options,
  className,
}: {
  options: OptionConfig[];
  className?: string;
}) {
  const primaryOption = options[0];
  const alternativeOptions = options.slice(1);

  // Wrap entire component in Popover so the panel can render outside overflow:hidden
  // PopoverPanel works with any positioned parent (relative, absolute, or fixed)
  return (
    <Popover className={classNames('pointer-events-auto', className)}>
      {({ open, close }) => (
        <>
          {/* Button group container */}
          <div className='flex overflow-hidden rounded-full bg-brand shadow-lg'>
            {/* Primary action button */}
            <button
              type='button'
              onClick={primaryOption.onClick}
              className={classNames(
                'flex items-center gap-2 px-5 py-3 text-white transition-colors',
                primaryOption.disabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-brand/90 active:bg-brand/80'
              )}
              aria-label={primaryOption.label}
              disabled={primaryOption.disabled}
              {...(primaryOption.dataUsetiful && {
                'data-usetiful': primaryOption.dataUsetiful,
              })}
            >
              {primaryOption.icon ?? (
                <FontAwesomeIcon icon={faPlus} className='h-4 w-4' />
              )}
              <span className='font-medium'>{primaryOption.label}</span>
            </button>

            {/* Divider and dropdown trigger for secondary actions */}
            {alternativeOptions.length > 0 && (
              <>
                <div className='w-px bg-white/30' />
                <PopoverButton
                  className='hover:bg-brand/90 active:bg-brand/80 flex h-12 items-center px-3 pr-3.5 text-white transition-colors focus:outline-none'
                  aria-label='More actions'
                >
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={classNames(
                      'h-3 w-3 transition-transform duration-200',
                      open && 'rotate-180'
                    )}
                  />
                </PopoverButton>
              </>
            )}
          </div>

          {/* Dropdown panel - rendered outside the overflow:hidden container */}
          {alternativeOptions.length > 0 && (
            <Transition
              as={Fragment}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <PopoverPanel className='absolute bottom-full right-0 mb-2 w-40 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
                <div className='py-1'>
                  {alternativeOptions.map((option) => (
                    <button
                      key={option.label}
                      type='button'
                      onClick={() => {
                        close();
                        option.onClick();
                      }}
                      disabled={option.disabled}
                      className={classNames(
                        'block w-full px-4 py-3 text-left text-sm transition-colors',
                        option.disabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'text-grey-70 hover:bg-grey-5 hover:text-brand'
                      )}
                      {...(option.dataUsetiful && {
                        'data-usetiful': option.dataUsetiful,
                      })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </PopoverPanel>
            </Transition>
          )}
        </>
      )}
    </Popover>
  );
}

/**
 * Default variant: Full-width stacking button (original implementation)
 */
function DefaultVariant({ options }: { options: OptionConfig[] }) {
  const styledOptions = options.map((option) => ({
    ...option,
    label: option.label.toLocaleUpperCase(),
  }));

  const primaryOption = styledOptions[0];
  const alternativeOptions = styledOptions.slice(1);

  const [optionsCollapsed, setOptionsCollapsed] = useState(false);
  const collapseOptions = () => setOptionsCollapsed(true);
  const expandOptions = () => setOptionsCollapsed(false);

  const { isMobile } = useBreakpoint();

  return (
    <Popover className={classNames(isMobile && 'flex flex-1', 'relative')}>
      {({ close }) => (
        <>
          <div
            className={classNames(
              !isMobile && 'flex-1',
              'relative z-10 mb-0 flex w-full font-semibold text-white md:mb-4'
            )}
          >
            <button
              type='button'
              className={classNames(
                'flex-grow rounded-lg bg-brand py-4 pl-1 shadow-[0px_4px_5px_rgba(0,0,0,0.16)] transition-colors hover:bg-[#007F7B] sm:pl-12',
                {
                  'rounded-l-lg rounded-r-none border-r border-white border-opacity-20':
                    alternativeOptions.length > 0,
                  'opacity-50': primaryOption.disabled,
                }
              )}
              onClick={primaryOption.onClick}
              disabled={primaryOption.disabled}
              {...(primaryOption.dataUsetiful && {
                'data-usetiful': primaryOption.dataUsetiful,
              })}
            >
              {primaryOption.label}
            </button>
            {alternativeOptions.length > 0 && (
              <PopoverButton
                className={classNames(
                  'w-10 rounded-r-lg bg-brand shadow-[0px_4px_5px_rgba(0,0,0,0.16)] transition-colors focus-within:outline-none hover:bg-[#007F7B] md:w-12'
                )}
              >
                <FontAwesomeIcon
                  className={classNames(
                    'transition-transform duration-300 ui-open:rotate-180'
                  )}
                  icon={faChevronUp}
                />
              </PopoverButton>
            )}
          </div>
          <Transition as={Fragment}>
            <PopoverPanel
              className={classNames(
                'text-white',
                isMobile && 'absolute left-0 right-0'
              )}
            >
              {alternativeOptions.map((option, index) => (
                <Transition.Child
                  as={Fragment}
                  enter='transition ease-in-out duration-300'
                  beforeEnter={collapseOptions}
                  enterFrom='opacity-0'
                  enterTo='opacity-100'
                  leave='transition ease-in-out duration-300'
                  beforeLeave={expandOptions}
                  leaveFrom='opacity-100'
                  leaveTo='opacity-0'
                  key={option.label}
                >
                  <button
                    type='button'
                    className={classNames(
                      'absolute top-0 w-full rounded-lg bg-brand py-4 font-semibold hover:bg-[#007F7B]',
                      {
                        'opacity-50': option.disabled,
                      }
                    )}
                    disabled={option.disabled}
                    onClick={() => {
                      close();
                      option.onClick();
                    }}
                    style={{
                      transform: optionsCollapsed
                        ? `translateY(-${4 * (index + 1)}rem)`
                        : 'translateY(0)',
                    }}
                    {...(option.dataUsetiful && {
                      'data-usetiful': option.dataUsetiful,
                    })}
                  >
                    {option.label}
                  </button>
                </Transition.Child>
              ))}
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
